import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, Upload, ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, XCircle, RefreshCcw, FileText } from "lucide-react";
import {
    scAmcMe, scAmcTemplateUrl, scAmcUploadInit, scAmcUploadChunk,
    scAmcUploadValidate, scAmcUploadSubmit,
} from "../../lib/sifcontrolApi";

const CHUNK_SIZE = 256 * 1024; // 256 KB base64 per chunk

const STATUS_BADGE = {
    ok: { cls: "bg-green-100 text-green-800", label: "Change" },
    warn: { cls: "bg-yellow-100 text-yellow-800", label: "Warning" },
    error: { cls: "bg-red-100 text-red-800", label: "Error" },
    unchanged: { cls: "bg-oth-bg-2 text-oth-ink3", label: "Unchanged" },
};

function blobToBase64Chunks(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const b64 = reader.result?.toString().split(",", 2)[1] || "";
            const chunks = [];
            for (let i = 0; i < b64.length; i += CHUNK_SIZE) {
                chunks.push(b64.slice(i, i + CHUNK_SIZE));
            }
            resolve(chunks);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

const StepDot = ({ active, done, label, idx }) => (
    <div className="flex items-center gap-2" data-testid={`sc-upload-step-${idx}`}>
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-body ${done ? "bg-[#0A0A0A] text-white" : active ? "bg-white border border-[#0A0A0A] text-[#0A0A0A]" : "bg-oth-bg-2 text-oth-ink3"}`}>
            {done ? "✓" : idx}
        </div>
        <span className={`text-[11px] uppercase tracking-[0.18em] font-body ${active || done ? "text-oth-ink" : "text-oth-ink3"}`}>{label}</span>
    </div>
);

const RowStatus = ({ row }) => {
    const meta = STATUS_BADGE[row.status] || STATUS_BADGE.unchanged;
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-body uppercase tracking-wider ${meta.cls}`}>{meta.label}</span>;
};

export default function SifControlAmcUpload() {
    const [me, setMe] = useState(null);
    const [step, setStep] = useState(1);
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [validating, setValidating] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [uploadDoc, setUploadDoc] = useState(null);
    const [error, setError] = useState("");
    const inputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        scAmcMe().then(setMe).catch(() => navigate("/sifcontrol/amc/login"));
    }, [navigate]);

    const reset = () => {
        setFile(null);
        setUploadDoc(null);
        setProgress(0);
        setError("");
        setStep(1);
    };

    const downloadTemplate = () => {
        const link = document.createElement("a");
        link.href = scAmcTemplateUrl("sif_meta");
        link.setAttribute("download", "sif_meta_template.csv");
        // Cookies must be sent; setting `download` attr + click works because
        // this hits our API on the same origin as REACT_APP_BACKEND_URL.
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Template download started.");
    };

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 10 * 1024 * 1024) {
            toast.error("File too large — 10 MB max.");
            return;
        }
        if (!/\.csv$/i.test(f.name)) {
            toast.error("Please choose a .csv file.");
            return;
        }
        setFile(f);
        setError("");
    };

    const startUpload = async () => {
        if (!file) return;
        setUploading(true);
        setError("");
        try {
            const chunks = await blobToBase64Chunks(file);
            const init = await scAmcUploadInit({
                kind: "sif_meta",
                filename: file.name,
                total_chunks: chunks.length,
                total_size: file.size,
            });
            setUploadDoc(init);
            for (let i = 0; i < chunks.length; i++) {
                await scAmcUploadChunk(init.id, i, chunks[i]);
                setProgress(Math.round(((i + 1) / chunks.length) * 100));
            }
            setValidating(true);
            const validated = await scAmcUploadValidate(init.id);
            setUploadDoc(validated);
            setValidating(false);
            setStep(3);
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Upload failed";
            setError(msg);
            toast.error(msg);
        } finally {
            setUploading(false);
            setValidating(false);
        }
    };

    const submitForReview = async () => {
        if (!uploadDoc) return;
        setSubmitting(true);
        try {
            await scAmcUploadSubmit(uploadDoc.id);
            toast.success("Submitted for admin review.");
            navigate("/sifcontrol/amc/uploads");
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not submit";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (!me) return <div className="p-16 text-center text-sm text-oth-ink3">Loading…</div>;

    const preview = uploadDoc?.preview;
    const summary = preview?.summary;
    const rows = preview?.rows || [];

    return (
        <section className="py-14 md:py-20" data-testid="sc-amc-upload-page">
            <div className="mx-auto max-w-[1080px] px-4 md:px-6">
                <button onClick={() => navigate("/sifcontrol/amc/dashboard")} className="text-xs font-body text-oth-ink3 hover:text-oth-ink inline-flex items-center gap-1 mb-4" data-testid="sc-amc-upload-back">
                    <ArrowLeft size={12} /> Back to dashboard
                </button>
                <div className="text-[11px] uppercase tracking-[0.22em] text-oth-ink3 font-body">Upload</div>
                <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tight text-oth-ink">SIF details CSV</h1>
                <p className="mt-2 text-sm font-body text-oth-ink2 max-w-[720px]">
                    Update fund-manager, benchmark, factsheet URL and other qualitative fields for your own SIFs. Every submission is queued for admin approval before it goes live.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-6 md:gap-8">
                    <StepDot idx={1} label="Template" active={step === 1} done={step > 1} />
                    <div className="h-px w-8 bg-oth-line" />
                    <StepDot idx={2} label="Upload" active={step === 2} done={step > 2} />
                    <div className="h-px w-8 bg-oth-line" />
                    <StepDot idx={3} label="Review" active={step === 3} done={step > 3} />
                </div>

                {step === 1 && (
                    <div className="mt-10 rounded-3xl border border-oth-line bg-white p-8" data-testid="sc-amc-upload-step-template">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body">Step 1 · Download template</div>
                        <div className="mt-2 font-display text-2xl text-oth-ink">Pre-filled with your SIFs</div>
                        <p className="mt-2 text-sm font-body text-oth-ink2">
                            The template contains one row per SIF under your AMC. Identity columns (SIF Code, ISIN, SIF Name) are read-only — used to locate the fund.
                            Blank cells mean “don't change”. Fill only what you want to update.
                        </p>
                        <ul className="mt-4 grid grid-cols-2 gap-x-8 gap-y-1 text-[12px] font-body text-oth-ink2 max-w-[720px]">
                            {["fund_manager", "co_fund_manager", "benchmark", "exit_load", "minimum_investment", "strategy_name", "investment_style", "investment_objective", "strategy_description", "factsheet_url"].map((f) => (
                                <li key={f}>• <code className="text-oth-ink">{f}</code></li>
                            ))}
                        </ul>
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                            <button onClick={downloadTemplate} className="btn-pill btn-pill-dark" data-testid="sc-amc-download-template">
                                <Download size={14} /> Download CSV template
                            </button>
                            <button onClick={() => setStep(2)} className="btn-pill" data-testid="sc-amc-upload-next">
                                I have my file <ArrowRight size={14} />
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="mt-10 rounded-3xl border border-oth-line bg-white p-8" data-testid="sc-amc-upload-step-upload">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body">Step 2 · Upload</div>
                        <div className="mt-2 font-display text-2xl text-oth-ink">Choose your edited CSV</div>
                        <p className="mt-2 text-sm font-body text-oth-ink2">Max 10 MB. File is streamed in 256 KB chunks.</p>

                        <div className="mt-6 border-2 border-dashed border-oth-line rounded-2xl p-8 text-center bg-oth-bg-2/30">
                            <FileText size={26} className="mx-auto text-oth-ink3" />
                            <div className="mt-3 text-sm font-body text-oth-ink">
                                {file ? file.name : "No file selected"}
                            </div>
                            {file && <div className="text-[11px] text-oth-ink3 font-body">{(file.size / 1024).toFixed(1)} KB</div>}
                            <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} data-testid="sc-amc-upload-file-input" />
                            <button onClick={() => inputRef.current?.click()} className="mt-4 btn-pill" data-testid="sc-amc-upload-choose">
                                {file ? "Choose a different file" : "Choose a CSV file"}
                            </button>
                        </div>

                        {uploading && (
                            <div className="mt-5" data-testid="sc-amc-upload-progress">
                                <div className="flex items-center justify-between text-[11px] font-body text-oth-ink3">
                                    <span>Uploading… {progress}%</span>
                                    {validating && <span>Validating…</span>}
                                </div>
                                <div className="mt-1 h-2 rounded-full bg-oth-bg-2 overflow-hidden">
                                    <div className="h-full bg-[#0A0A0A] transition-all" style={{ width: `${progress}%` }} />
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="mt-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-[12px] font-body px-3 py-2" data-testid="sc-amc-upload-error">{error}</div>
                        )}

                        <div className="mt-6 flex items-center gap-3">
                            <button onClick={() => setStep(1)} className="btn-pill" data-testid="sc-amc-upload-back-btn">
                                <ArrowLeft size={14} /> Back
                            </button>
                            <button
                                onClick={startUpload}
                                disabled={!file || uploading || validating}
                                className="btn-pill btn-pill-dark"
                                data-testid="sc-amc-upload-start"
                            >
                                <Upload size={14} /> {uploading ? "Uploading…" : validating ? "Validating…" : "Upload and validate"}
                            </button>
                        </div>
                    </div>
                )}

                {step === 3 && preview && (
                    <div className="mt-10 space-y-6" data-testid="sc-amc-upload-step-review">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                { label: "Rows", value: summary.total, cls: "" },
                                { label: "Ready to apply", value: summary.ok + summary.warn, cls: "text-green-800" },
                                { label: "Unchanged", value: summary.unchanged, cls: "text-oth-ink3" },
                                { label: "Errors", value: summary.error, cls: "text-red-700" },
                            ].map((t) => (
                                <div key={t.label} className={`rounded-2xl border border-oth-line bg-white p-4 ${t.cls}`}>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-oth-ink3 font-body">{t.label}</div>
                                    <div className="mt-1 font-display text-2xl font-semibold">{t.value}</div>
                                </div>
                            ))}
                        </div>

                        <div className="rounded-3xl border border-oth-line bg-white overflow-hidden">
                            <div className="px-5 py-3 border-b border-oth-line/70 flex items-center justify-between">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body">Preview · {uploadDoc.filename}</div>
                                <div className="text-[11px] font-body text-oth-ink3">Green rows will be applied on approval. Errors block submission.</div>
                            </div>
                            <div className="max-h-[540px] overflow-auto thin-scroll">
                                {rows.length === 0 ? (
                                    <div className="p-8 text-center text-sm font-body text-oth-ink3">No rows.</div>
                                ) : (
                                    <table className="w-full text-[12px] font-body" data-testid="sc-amc-upload-preview-table">
                                        <thead className="text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body bg-oth-bg-2/60">
                                            <tr>
                                                <th className="text-left px-4 py-2">Row</th>
                                                <th className="text-left px-4 py-2">SIF</th>
                                                <th className="text-left px-4 py-2">Status</th>
                                                <th className="text-left px-4 py-2">Changes / Errors</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r) => (
                                                <tr key={`${r.row_number}-${r.sif_code}`} className="border-t border-oth-line/70 align-top" data-testid={`sc-amc-upload-row-${r.row_number}`}>
                                                    <td className="px-4 py-3 text-oth-ink3">{r.row_number}</td>
                                                    <td className="px-4 py-3">
                                                        <div className="font-medium text-oth-ink">{r.sif_code || "—"}</div>
                                                        <div className="text-[11px] text-oth-ink3">{r.sif_name || ""}</div>
                                                    </td>
                                                    <td className="px-4 py-3"><RowStatus row={r} /></td>
                                                    <td className="px-4 py-3 space-y-1">
                                                        {r.errors?.map((e, i) => (
                                                            <div key={`e-${i}`} className="text-red-700 flex items-start gap-1"><XCircle size={12} className="mt-0.5 shrink-0" />{e}</div>
                                                        ))}
                                                        {r.warnings?.map((w, i) => (
                                                            <div key={`w-${i}`} className="text-yellow-800 flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{w}</div>
                                                        ))}
                                                        {r.changes?.map((c, i) => (
                                                            <div key={`c-${i}`} className="text-oth-ink2">
                                                                <span className="text-oth-ink3">{c.field}:</span>{" "}
                                                                <span className="line-through text-oth-ink3">{String(c.old || "—")}</span>{" "}
                                                                → <span className="text-green-800">{String(c.new || "—")}</span>
                                                            </div>
                                                        ))}
                                                        {r.status === "unchanged" && (r.errors?.length ?? 0) === 0 && (
                                                            <div className="text-oth-ink3">No changes for this SIF.</div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                            <button onClick={reset} className="btn-pill" data-testid="sc-amc-upload-restart">
                                <RefreshCcw size={14} /> Upload another file
                            </button>
                            <button
                                onClick={submitForReview}
                                disabled={submitting || (summary.error > 0) || (summary.ok + summary.warn === 0)}
                                className="btn-pill btn-pill-dark disabled:opacity-40"
                                data-testid="sc-amc-upload-submit"
                            >
                                <CheckCircle2 size={14} /> {submitting ? "Submitting…" : "Submit for admin review"}
                            </button>
                            {summary.error > 0 && <span className="text-[12px] text-red-700 font-body">Fix errors above and re-upload.</span>}
                            {summary.error === 0 && summary.ok + summary.warn === 0 && <span className="text-[12px] text-oth-ink3 font-body">Nothing to apply — every row is unchanged.</span>}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
